import { createContext, useContext, useState, useEffect, useRef, ReactNode, useMemo, useCallback } from 'react'
import type { VariableAllocationTable } from 'types/comp-allocations'
import type { ExpandedTarget } from 'types/components'
import type {
  CompensationTemplate,
  UpdateCompensationTemplate,
  CompensationTemplateDTO,
  ExpandedCompensationTemplateDTO,
} from 'types/comp-template'
import type { Period } from 'types/periods'
import { createIdToPeriodFromList, getFlattenedSubtree } from 'types/periods'
import useAuth from 'hooks/useAuth'
import type { UserProfile } from 'types/user-profile'
import { getOrganizationLabelMap } from 'types/user-profile'
import { useGlobalState } from 'contexts/GlobalStateContext'
import { fetchWithToken, putWithToken, postWithToken } from 'hooks/http'
import { useFetchOrganizationCoreSettings } from 'hooks/useOrganizations'
import { useNavigate, useLocation } from 'react-router-dom'
import type { OrganizationCoreSetting } from 'types/organization'
import { usePeriods } from 'hooks/usePeriods'
import { RowType } from 'types/comp-allocations'

interface ValidationResult {
  isValid: boolean
  message: string
  color?: string
  backgroundColor?: string
}

interface EditTemplateState {
  // Mode
  isCopyMode: boolean
  isLoadingTemplate: boolean
  loadTemplateError: boolean
  errorValue: any | null
  templateId: string | null

  // validation checks
  isValidTemplate: boolean
  validationResult: ValidationResult

  // Basic template details
  label: string
  position: string

  // Organization and period settings
  organizationIds: string[]
  rootOrganizationId: string
  periodId: string
  periodProration: number
  rootPeriodId: string
  period: Period | null

  // Compensation settings
  compensationModel: string
  variablePercent: number
  minVariablePercent: number
  maxVariablePercent: number
  defaultVariableCap: number
  minVariableCap: number
  maxVariableCap: number

  // Example values
  exampleSalary: number
  calculatedBaseComp: number
  calculatedVariableComp: number

  // Currency settings
  currency: string
  currencyDecimals: number

  // Allocation tables
  allocationTables: VariableAllocationTable[]

  // Target management
  expandedTargetsOrgId: string
  expandedTargetsPeriodId: string
  allSelectedTargets: ExpandedTarget[]

  // Auth state
  profile: UserProfile | undefined
  organizationMap: Record<string, string>

  // Period Information
  periods: Period[]

  // Loading state of context
  isLoading: boolean
  alteredState: boolean

  // Save state
  isSaving: boolean
  saveError: string | null
}

interface EditTemplateActions {
  // Basic template details
  setLabel: (label: string) => void
  setPosition: (position: string) => void

  // Organization and period settings
  setOrganizationIds: (organizationIds: string[]) => void
  setRootOrganizationId: (rootOrganizationId: string) => void
  setPeriodId: (periodId: string) => void
  setPeriodProration: (periodProration: number) => void
  setRootPeriodId: (rootPeriodId: string) => void
  setPeriod: (period: Period | null) => void

  // Compensation settings
  setCompensationModel: (compensationModel: string) => void
  setVariablePercent: (variablePercent: number) => void
  setMinVariablePercent: (minVariablePercent: number) => void
  setMaxVariablePercent: (maxVariablePercent: number) => void
  setDefaultVariableCap: (defaultVariableCap: number) => void
  setMinVariableCap: (minVariableCap: number) => void
  setMaxVariableCap: (maxVariableCap: number) => void

  // Example values
  setExampleSalary: (exampleSalary: number) => void
  setCalculatedBaseComp: (calculatedBaseComp: number) => void
  setCalculatedVariableComp: (calculatedVariableComp: number) => void

  // Currency settings
  setCurrency: (currency: string) => void
  setCurrencyDecimals: (currencyDecimals: number) => void

  // Allocation tables
  setAllocationTables: (allocationTables: VariableAllocationTable[]) => void

  // Target management
  setExpandedTargetsOrgId: (expandedTargetsOrgId: string) => void
  setExpandedTargetsPeriodId: (expandedTargetsPeriodId: string) => void
  setAllSelectedTargets: (allSelectedTargets: ExpandedTarget[]) => void

  updateAllocationTable: (updatedTable: VariableAllocationTable) => void
  saveContext: () => void
  resetAlteredState: () => void
}

type EditTemplateContextType = EditTemplateState & EditTemplateActions

const EditTemplateContext = createContext<EditTemplateContextType | undefined>(undefined)

interface EditTemplateV2ProviderProps {
  children: ReactNode
  templateId: string | null
}

export const EditTemplateProvider: React.FC<EditTemplateV2ProviderProps> = ({ children, templateId }) => {
  const navigate = useNavigate()
  const location = useLocation()

  const [isLoading, setIsLoading] = useState(false)
  const [errorValue, setErrorValue] = useState<any | null>(null)
  const [alteredState, setAlteredState] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  // Copy mode: when route state provides template data for copying
  const copyData = (location.state as any)?.copyTemplateData ?? null
  const [isCopyMode] = useState(!!copyData)

  // Add this flag to track when initial data loading is complete
  const [initialLoadComplete, setInitialLoadComplete] = useState(false)

  // Use ref to track original state for change detection
  const originalStateRef = useRef<any>(null)

  const [loadTemplateError, setLoadTemplateError] = useState(false)
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false)

  const [isValidTemplate, setIsValidTemplate] = useState(false)
  const [validationResult, setValidationResult] = useState<ValidationResult>({
    isValid: false,
    message: '',
  })

  // Original template metadata (preserved for PUT updates)
  const originalTemplateMetaRef = useRef<{ createdAt: string; updatedAt: string }>({
    createdAt: '',
    updatedAt: '',
  })

  // Template details
  const [label, setLabel] = useState('')
  const [position, setPosition] = useState('')
  const [organizationIds, setOrganizationIds] = useState<string[]>([])
  const [rootOrganizationId, setRootOrganizationId] = useState('')
  const [periodId, setPeriodId] = useState('')
  const [periodProration, setPeriodProration] = useState(1)
  const [rootPeriodId, setRootPeriodId] = useState('')
  const [period, setPeriod] = useState<Period | null>(null)
  const [compensationModel, setCompensationModel] = useState('BASE_PLUS')
  const [variablePercent, setVariablePercent] = useState<number>(0.1)
  const [minVariablePercent, setMinVariablePercent] = useState<number>(0.05)
  const [maxVariablePercent, setMaxVariablePercent] = useState<number>(0.15)
  const [defaultVariableCap, setDefaultVariableCap] = useState<number>(1.5)
  const [minVariableCap, setMinVariableCap] = useState<number>(1.2)
  const [maxVariableCap, setMaxVariableCap] = useState<number>(2.0)
  const [exampleSalary, setExampleSalary] = useState(100000)
  const [calculatedBaseComp, setCalculatedBaseComp] = useState(0)
  const [calculatedVariableComp, setCalculatedVariableComp] = useState(0)
  const [currency, setCurrency] = useState('USD')
  const [currencyDecimals, setCurrencyDecimals] = useState(2)
  const [allocationTables, setAllocationTables] = useState<VariableAllocationTable[]>([])
  const [expandedTargetsOrgId, setExpandedTargetsOrgId] = useState('')
  const [expandedTargetsPeriodId, setExpandedTargetsPeriodId] = useState('')
  const [allSelectedTargets, setAllSelectedTargets] = useState<ExpandedTarget[]>([])

  // Information need for the template forms
  // Auth and State
  const { purcentAppState } = useGlobalState()
  const { profile, isLoading: isLoadingProfile, token } = useAuth()
  const [organizationMap, setOrganizationMap] = useState<Record<string, string>>({})

  // Period information for select
  const { periods: periodResponse, isLoadingPeriods } = usePeriods(
    token!,
    purcentAppState?.selectedOrganization?.rootOrganizationId || ''
  )
  const [periods, setPeriods] = useState<Period[]>([])

  // Handling of the organization's default settings for templates
  const [orgSettings, setOrgSettings] = useState<OrganizationCoreSetting | null>(null)
  const [isLoadingOrgSettings, setIsLoadingOrgSettings] = useState(false)
  const [settingsInitialized, setSettingsInitialized] = useState(false)
  const { coreSettings: fetchedOrgSettings, isLoading: isFetchingOrgSettings } = useFetchOrganizationCoreSettings(
    organizationIds.length > 0
      ? // Use selected organization ID first
        [organizationIds[0]]
      : purcentAppState?.selectedOrganization?.rootOrganizationId
        ? // Use root organization ID is nothing selected
          [purcentAppState?.selectedOrganization?.rootOrganizationId]
        : // Finally default to an empty array
          [],
    token!
  )

  const updateAllocationTable = useCallback((updatedTable: VariableAllocationTable) => {
    // This does not seem to be seeting as expected because the state thinks it has changed but it hasn't
    // Could it be the updatedTables needs to be fanned out.
    setAllocationTables((prevTables) => {
      const updatedTables = [...prevTables]
      const index = updatedTables.findIndex((table) => table.id === updatedTable.id)
      if (index !== -1) {
        updatedTables[index] = updatedTable
      }
      return [...updatedTables]
    })
  }, [])

  const getActiveTargets = useCallback((): ExpandedTarget[] => {
    return allSelectedTargets.filter((target) => {
      const targetSlug = target.target.slug
      const componentSlug = target.component.slug

      return allocationTables.some((table) =>
        table.rows.some((row) => {
          const requirementMatch = row.formulaRequirements.some(
            (req) => req.includes(targetSlug) || req.includes(componentSlug)
          )
          const optionMatch = row.formulaOptions.some((opt) => opt.includes(targetSlug) || opt.includes(componentSlug))
          return requirementMatch || optionMatch
        })
      )
    })
  }, [allSelectedTargets, allocationTables])

  const collectComponentIds = useCallback((): string[] => {
    const componentIds = getActiveTargets()
      .map((target) => target.component.id)
      .filter((id): id is string => !!id)
    return [...new Set(componentIds)]
  }, [getActiveTargets])

  const saveContext = useCallback(() => {
    if (!token) return

    setIsSaving(true)
    setSaveError(null)

    const componentIds = collectComponentIds()

    if (templateId && !isCopyMode) {
      // Edit mode: PUT request
      const template: CompensationTemplate = {
        id: templateId,
        revision: 0,
        createdAt: originalTemplateMetaRef.current.createdAt,
        updatedAt: originalTemplateMetaRef.current.updatedAt,
        rootOrganizationId,
        organizationIds,
        rootPeriodId,
        periodProration,
        label,
        periodId,
        position,
        compensationModel,
        variablePercent,
        minVariablePercent,
        maxVariablePercent,
        variableCap: defaultVariableCap,
        minVariableCap,
        maxVariableCap,
        variableAllocations: allocationTables,
      }

      putWithToken(`compensation/templates/${templateId}`, token, {
        template,
        components: componentIds,
      } as UpdateCompensationTemplate)
        .then(() => {
          setAlteredState(false)
          navigate('/admin/templates')
        })
        .catch((error) => {
          setSaveError(error?.message || 'Failed to save template')
        })
        .finally(() => {
          setIsSaving(false)
        })
    } else {
      // Create mode (new or copy): POST request
      const allocations = allocationTables.map((table) => {
        const newTable = { ...table }
        newTable.id = undefined
        newTable.rows = newTable.rows.map((row) => {
          const newRow = { ...row }
          newRow.id = undefined
          return newRow
        })
        return newTable
      })

      const templateDTO: CompensationTemplateDTO = {
        rootOrganizationId,
        organizationIds,
        rootPeriodId,
        label,
        periodId,
        periodProration,
        position,
        compensationModel,
        variablePercent,
        minVariablePercent,
        maxVariablePercent,
        variableCap: defaultVariableCap,
        minVariableCap,
        maxVariableCap,
        variableAllocations: allocations,
      }

      postWithToken('compensation/templates', token, {
        template: templateDTO,
        components: componentIds,
      } as ExpandedCompensationTemplateDTO)
        .then(() => {
          setAlteredState(false)
          navigate('/admin/templates')
        })
        .catch((error) => {
          setSaveError(error?.message || 'Failed to create template')
        })
        .finally(() => {
          setIsSaving(false)
        })
    }
  }, [
    token,
    templateId,
    isCopyMode,
    collectComponentIds,
    rootOrganizationId,
    organizationIds,
    rootPeriodId,
    periodProration,
    label,
    periodId,
    position,
    compensationModel,
    variablePercent,
    minVariablePercent,
    maxVariablePercent,
    defaultVariableCap,
    minVariableCap,
    maxVariableCap,
    allocationTables,
    navigate,
  ])

  const resetAlteredState = useCallback(() => {
    setAlteredState(false)
    // Reset the original state to current state
    const currentState = {
      label,
      position,
      organizationIds,
      rootOrganizationId,
      periodId,
      periodProration,
      rootPeriodId,
      compensationModel,
      variablePercent,
      minVariablePercent,
      maxVariablePercent,
      defaultVariableCap,
      minVariableCap,
      maxVariableCap,
      exampleSalary,
      currency,
      currencyDecimals,
      allocationTables,
      expandedTargetsOrgId,
      expandedTargetsPeriodId,
      allSelectedTargets,
    }
    originalStateRef.current = JSON.parse(JSON.stringify(currentState))
  }, [
    label,
    position,
    organizationIds,
    rootOrganizationId,
    periodId,
    periodProration,
    rootPeriodId,
    compensationModel,
    variablePercent,
    minVariablePercent,
    maxVariablePercent,
    defaultVariableCap,
    minVariableCap,
    maxVariableCap,
    exampleSalary,
    currency,
    currencyDecimals,
    allocationTables,
    expandedTargetsOrgId,
    expandedTargetsPeriodId,
    allSelectedTargets,
  ])

  const contextValue: EditTemplateContextType = {
    // State
    isCopyMode,
    templateId,
    isLoadingTemplate, // READ ONLY
    loadTemplateError, // READ ONLY
    errorValue, // READ ONLY
    isValidTemplate,
    validationResult,
    label,
    position,
    organizationIds,
    rootOrganizationId,
    periodId,
    periodProration,
    rootPeriodId,
    period,
    compensationModel,
    variablePercent,
    minVariablePercent,
    maxVariablePercent,
    defaultVariableCap,
    minVariableCap,
    maxVariableCap,
    exampleSalary,
    calculatedBaseComp,
    calculatedVariableComp,
    currency,
    currencyDecimals,
    allocationTables,
    expandedTargetsOrgId,
    expandedTargetsPeriodId,
    allSelectedTargets,
    profile, // READ ONLY
    organizationMap, // READ ONLY
    periods, // READ ONLY
    isLoading, // READ ONLY
    alteredState, // READ ONLY
    isSaving,
    saveError,

    // Actions
    setLabel,
    setPosition,
    setOrganizationIds,
    setRootOrganizationId,
    setPeriodId,
    setPeriodProration,
    setRootPeriodId,
    setPeriod,
    setCompensationModel,
    setVariablePercent,
    setMinVariablePercent,
    setMaxVariablePercent,
    setDefaultVariableCap,
    setMinVariableCap,
    setMaxVariableCap,
    setExampleSalary,
    setCalculatedBaseComp,
    setCalculatedVariableComp,
    setCurrency,
    setCurrencyDecimals,
    setAllocationTables,
    setExpandedTargetsOrgId,
    setExpandedTargetsPeriodId,
    setAllSelectedTargets,

    updateAllocationTable,
    saveContext,
    resetAlteredState,
  }

  useEffect(() => {
    if (templateId && templateId !== '' && token) {
      fetchTemplateData()
    } else if (!templateId) {
      setIsLoadingTemplate(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, token])

  // Initialize from copy data if in copy mode
  useEffect(() => {
    if (copyData && !templateId) {
      const data = copyData
      if (data.template) {
        data.template.variableAllocations = preprocessAllocationTables(data.template.variableAllocations || [])

        setLabel(`Copy of ${data.template.label}`)
        setPosition(data.template.position)
        setOrganizationIds(data.template.organizationIds || [])
        setRootOrganizationId(data.template.rootOrganizationId)
        setPeriodId(data.template.periodId)
        setRootPeriodId(data.template.rootPeriodId)
        setPeriodProration(data.template.periodProration)
        setCompensationModel(data.template.compensationModel)
        setVariablePercent(data.template.variablePercent)
        setMinVariablePercent(data.template.minVariablePercent)
        setMaxVariablePercent(data.template.maxVariablePercent)
        setDefaultVariableCap(data.template.variableCap)
        setMinVariableCap(data.template.minVariableCap)
        setMaxVariableCap(data.template.maxVariableCap)
        setAllocationTables(data.template.variableAllocations)

        if (data.period) {
          const periodMap = createIdToPeriodFromList([data.period])
          const compensationPeriod = periodMap[data.template.periodId]
          setPeriod(compensationPeriod)
        }

        if (data.targets && data.components) {
          const newExpandedTargets = data.targets.map((target: any) => ({
            target,
            component: data.components.find((comp: any) => comp.id === target.componentId),
          }))
          setAllSelectedTargets(newExpandedTargets)
        }

        setSettingsInitialized(true)
        setAlteredState(false)
        setTimeout(() => {
          setInitialLoadComplete(true)
        }, 0)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copyData])

  // Initialize template forms state if in createMode once the core settings are loaded
  useEffect(() => {
    if (!templateId && !isFetchingOrgSettings && fetchedOrgSettings && fetchedOrgSettings.length > 0) {
      setOrgSettings(fetchedOrgSettings[0])
      setIsLoadingOrgSettings(false)
    } else if (!templateId && !isFetchingOrgSettings) {
      setIsLoadingOrgSettings(false)
    }
  }, [fetchedOrgSettings, isFetchingOrgSettings, templateId])

  // Set the defaults when the org settings are loaded
  useEffect(() => {
    if (!templateId && !isLoadingOrgSettings && orgSettings && !settingsInitialized) {
      setCompensationModel(orgSettings.defaultCompensationModel)
      setExampleSalary(orgSettings.defaultExampleCompensation)
      setCurrency(orgSettings.currency)
      setCurrencyDecimals(orgSettings.currencyDecimals)

      // Handle defaultVariableCompensation
      const variableCompValues = orgSettings.defaultVariableCompensation.split(',').map(Number)
      if (variableCompValues.length >= 3) {
        setMinVariablePercent(variableCompValues[0])
        setVariablePercent(variableCompValues[1])
        setMaxVariablePercent(variableCompValues[2])
      }

      // Handle defaultMaxPayout
      const maxPayoutValues = orgSettings.defaultMaxPayout.split(',').map(Number)
      if (maxPayoutValues.length >= 3) {
        setMinVariableCap(maxPayoutValues[0])
        setDefaultVariableCap(maxPayoutValues[1])
        setMaxVariableCap(maxPayoutValues[2])
      }

      if (
        orgSettings.defaultTemplateTables &&
        orgSettings.defaultTemplateTables.length > 0 &&
        allocationTables.length === 0
      ) {
        const defaultTables = orgSettings.defaultTemplateTables.split(',')
        setAllocationTables(
          defaultTables
            .filter((table) => table !== '')
            .map((table) => table.trim())
            .map((table) => ({
              label: table,
              rows: [],
            }))
        )
      } else {
        // Initialze to inlclude 1 section labeled Default
        setAllocationTables([
          {
            label: 'Default',
            rows: [],
          },
        ])
      }

      setSettingsInitialized(true)

      // For new templates, mark initial load as complete after settings are initialized
      setAlteredState(false)

      // Set initial state reference before marking load as complete
      setTimeout(() => {
        setInitialLoadComplete(true)
      }, 0)
    } else if (!templateId && !isLoadingOrgSettings && !orgSettings && !settingsInitialized) {
      // Initialize with basic defaults if no org settings available
      setAllocationTables([
        {
          label: 'Default',
          rows: [],
        },
      ])
      setSettingsInitialized(true)
      setAlteredState(false)
      setTimeout(() => {
        setInitialLoadComplete(true)
      }, 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgSettings, isLoadingOrgSettings, templateId, settingsInitialized])

  useEffect(() => {
    if (!isLoadingProfile && profile) {
      setOrganizationMap(getOrganizationLabelMap(profile))
    }
  }, [isLoadingProfile, profile])

  // Update isLoading when profile or periods loading state changes
  useEffect(() => {
    const loading = isLoadingProfile || isLoadingPeriods || isLoadingTemplate || isLoadingOrgSettings
    setIsLoading(loading)
  }, [isLoadingProfile, isLoadingPeriods, isLoadingTemplate, isLoadingOrgSettings])

  // Track state changes after initial load using refs
  useEffect(() => {
    if (!initialLoadComplete) return

    const currentState = {
      label,
      position,
      organizationIds,
      rootOrganizationId,
      periodId,
      periodProration,
      rootPeriodId,
      compensationModel,
      variablePercent,
      minVariablePercent,
      maxVariablePercent,
      defaultVariableCap,
      minVariableCap,
      maxVariableCap,
      exampleSalary,
      currency,
      currencyDecimals,
      allocationTables,
      expandedTargetsOrgId,
      expandedTargetsPeriodId,
      allSelectedTargets,
    }

    // Set original state reference when initial load completes
    if (!originalStateRef.current) {
      originalStateRef.current = JSON.parse(JSON.stringify(currentState))
      setAlteredState(false)
      return
    }

    // Compare current state with original state
    const currentStateStr = JSON.stringify(currentState)
    const originalStateStr = JSON.stringify(originalStateRef.current)
    const hasChanged = currentStateStr !== originalStateStr

    setAlteredState(hasChanged)
  }, [
    initialLoadComplete,
    label,
    position,
    organizationIds,
    rootOrganizationId,
    periodId,
    periodProration,
    rootPeriodId,
    compensationModel,
    variablePercent,
    minVariablePercent,
    maxVariablePercent,
    defaultVariableCap,
    minVariableCap,
    maxVariableCap,
    exampleSalary,
    currency,
    currencyDecimals,
    allocationTables,
    expandedTargetsOrgId,
    expandedTargetsPeriodId,
    allSelectedTargets,
  ])

  useEffect(() => {
    if (!isLoadingPeriods && periodResponse) {
      setPeriods(periodResponse.periods)
    }
  }, [isLoadingPeriods, periodResponse])

  // Filter targets when period or organization changes mid-edit
  // Removes targets whose rootOrganizationId doesn't match or whose periodId
  // isn't in the template's period subtree (mirroring V1 EditTemplate behavior)
  useEffect(() => {
    if (rootOrganizationId && periodId && periods.length > 0) {
      const subtreePeriods = getFlattenedSubtree(periods, rootPeriodId, periodId)
      const validPeriodIds = new Set(subtreePeriods.map((p) => p.id).filter(Boolean))

      setAllSelectedTargets((prevTargets) => {
        const filtered = prevTargets.filter((target) => {
          const orgMatch = target.target.rootOrganizationId === rootOrganizationId
          const rootPeriodMatch = target.target.rootPeriodId === rootPeriodId
          const targetPeriodInSubtree = target.target.periodId && validPeriodIds.has(target.target.periodId)
          return orgMatch && rootPeriodMatch && targetPeriodInSubtree
        })
        // Only update if something was actually filtered out
        if (filtered.length === prevTargets.length) return prevTargets
        return filtered
      })
    }
  }, [rootOrganizationId, periodId, rootPeriodId, periods])

  // Memoized validation functions
  const validateAllocations = useMemo((): ValidationResult => {
    let totalAllocation = 0
    let incompleteRows = 0

    if (allocationTables.length === 0) {
      return { isValid: false, message: 'Status: Please add at least one Section' }
    }
    // Check if any table has no rows
    for (const table of allocationTables) {
      if (table.rows.length === 0) {
        return { isValid: false, message: 'Status: Please add a row to a section' }
      }
    }

    allocationTables.forEach((table) => {
      table.rows.forEach((row) => {
        if (row.allocationPercentage) {
          totalAllocation += row.allocationPercentage
        }
        if (
          !row.label ||
          !row.periodId ||
          (!row.allocationPercentage && row.rowType !== RowType.DISPLAY_ROW) ||
          row.formulaOptions.length === 0
        ) {
          incompleteRows++
        }
      })
    })

    // Use four decimal precision for total allocation
    const roundedTotal = Math.round(totalAllocation * 10000) / 10000 // e.g. 0.9999, 1.0000
    const isTotalValid = roundedTotal === 1.0 // must be exactly 1.0000

    const roundedTotalPercentage = (roundedTotal * 100).toFixed(2)

    if (!isTotalValid) {
      return {
        isValid: false,
        message: `Status: Total allocation must be exactly 100%. Current total: ${roundedTotalPercentage}%`,
      }
    }

    if (incompleteRows > 0) {
      return { isValid: false, message: `${incompleteRows} row(s) are incomplete` }
    }

    return { isValid: true, message: 'All allocations are valid' }
  }, [allocationTables])

  const checkTemplateValidity = useMemo(() => {
    // check that all the required data fields have been entered
    // Check basic template details
    if (!label || label.trim() === '') return false
    if (!position || position.trim() === '') return false

    // Check organization and period settings
    if (!organizationIds || organizationIds.length === 0) return false
    if (!rootOrganizationId || rootOrganizationId.trim() === '') return false
    if (!periodId || periodId.trim() === '') return false
    if (!rootPeriodId || rootPeriodId.trim() === '') return false
    if (!period) return false
    if (periodProration <= 0) return false

    // Check compensation settings
    if (!compensationModel || compensationModel.trim() === '') return false
    if (variablePercent < 0 || variablePercent > 1.0) return false
    if (minVariablePercent > maxVariablePercent || minVariablePercent < 0 || minVariablePercent > 1.0) return false
    if (maxVariablePercent < minVariablePercent || maxVariablePercent < 0 || maxVariablePercent > 1.0) return false
    if (defaultVariableCap < 0) return false
    if (minVariableCap > maxVariableCap || minVariableCap < 0) return false
    if (maxVariableCap < minVariableCap || maxVariableCap < 0) return false

    // Check example values
    if (exampleSalary <= 0) return false

    // Check currency settings
    if (!currency || currency.trim() === '') return false
    if (currencyDecimals <= 0) return false

    // Check allocation tables
    if (!allocationTables || allocationTables.length === 0) return false

    // Check validation state
    if (validateAllocations.isValid === false) return false

    return true
  }, [
    label,
    position,
    organizationIds,
    rootOrganizationId,
    periodId,
    rootPeriodId,
    period,
    periodProration,
    compensationModel,
    variablePercent,
    minVariablePercent,
    maxVariablePercent,
    defaultVariableCap,
    minVariableCap,
    maxVariableCap,
    exampleSalary,
    currency,
    currencyDecimals,
    allocationTables,
    validateAllocations,
  ])

  // Update validation result when it changes
  useEffect(() => {
    setValidationResult(validateAllocations)
  }, [validateAllocations])

  // Update template validity when it changes
  useEffect(() => {
    setIsValidTemplate(checkTemplateValidity)
  }, [checkTemplateValidity])

  useEffect(() => {
    if (periodId && exampleSalary && variablePercent) {
      const bonusAmount = exampleSalary * variablePercent
      const baseSalary = compensationModel === 'BASE_PLUS' ? exampleSalary : exampleSalary - bonusAmount
      setCalculatedBaseComp(baseSalary * periodProration)
      setCalculatedVariableComp(bonusAmount * periodProration)
    } else {
      setCalculatedBaseComp(0) // Default for new templates
      setCalculatedVariableComp(0) // Default for new templates
    }
  }, [periodId, exampleSalary, variablePercent, compensationModel, periodProration])

  const fetchTemplateData = async () => {
    setIsLoadingTemplate(true)
    setLoadTemplateError(false)
    setErrorValue(null)

    try {
      const data = await fetchWithToken(`compensation/templates/${templateId}`, token!, null)

      setTemplateData(data)

      if (data.period) {
        const periodMap = createIdToPeriodFromList([data.period])
        const compensationPeriod = periodMap[data.template.periodId]
        setPeriod(compensationPeriod)
      }

      const newExpandedTargets = data.targets.map((target: any) => ({
        target,
        component: data.components.find((comp: any) => comp.id === target.componentId),
      }))
      setAllSelectedTargets(newExpandedTargets)

      setIsLoadingTemplate(false)
    } catch (error) {
      setErrorValue(error)
      setLoadTemplateError(true)
      setIsLoadingTemplate(false)
    }
  }

  const preprocessAllocationTables = (tables: VariableAllocationTable[]) => {
    return tables.map((table) => ({
      ...table,
      id: table.id ?? crypto.randomUUID(),
      rows: table.rows.map((row, index) => ({
        ...row,
        templateAllocationRowId: row.templateAllocationRowId ?? crypto.randomUUID(),
        order: row.order ?? index,
      })),
    }))
  }

  const setTemplateData = (data: any) => {
    if (data) {
      data.template.variableAllocations = preprocessAllocationTables(data.template.variableAllocations)

      // Preserve original metadata for PUT updates
      originalTemplateMetaRef.current = {
        createdAt: data.template.createdAt || '',
        updatedAt: data.template.updatedAt || '',
      }

      setLabel(data.template.label)
      setPosition(data.template.position)
      setOrganizationIds(data.template.organizationIds)
      setRootOrganizationId(data.template.rootOrganizationId)
      setPeriodId(data.template.periodId)
      setRootPeriodId(data.template.rootPeriodId)
      setPeriodProration(data.template.periodProration)
      setCompensationModel(data.template.compensationModel)
      setVariablePercent(data.template.variablePercent)
      setMinVariablePercent(data.template.minVariablePercent)
      setMaxVariablePercent(data.template.maxVariablePercent)
      setDefaultVariableCap(data.template.variableCap)
      setMinVariableCap(data.template.minVariableCap)
      setMaxVariableCap(data.template.maxVariableCap)
      setAllocationTables(data.template.variableAllocations)

      // Reset alteredState and mark initial load as complete
      setAlteredState(false)

      // Set initial state reference before marking load as complete
      setTimeout(() => {
        setInitialLoadComplete(true)
      }, 0)
    }
  }

  return <EditTemplateContext.Provider value={contextValue}>{children}</EditTemplateContext.Provider>
}

export const useEditTemplate = (): EditTemplateContextType => {
  const context = useContext(EditTemplateContext)
  if (context === undefined) {
    throw new Error('useEditTemplateV2 must be used within an EditTemplateV2Provider')
  }
  return context
}
